// src/components/ordering/PlaceOrderModal.tsx
import { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { WarehouseSelector } from "@/components/ordering/WarehouseSelector";
import {
  StandaloneOrderService,
  StandaloneOrderVendorGroup,
  StandaloneOrderLineItem,
} from "@/services/standalone-order.service";
import type { RegistryProduct } from "@/data/ProductRegistryContext";
import type { ProductRegistryService } from "@/services/productRegistry.service";
import type { POTrackingEntry, POTrackingPurpose } from "@/data/VendorPOContext";

// ── Tracking helpers (mirrors OrderingQueue.tsx) ──────────────────────────
const TRACKING_PURPOSE_LABELS: Record<POTrackingPurpose, string> = {
  inbound: "Inbound",
  outbound: "Outbound",
  return: "Return",
  reship: "Reship",
  replacement: "Replacement",
  other: "Other",
};
const TRACKING_PURPOSE_OPTIONS = Object.entries(TRACKING_PURPOSE_LABELS) as Array<[POTrackingPurpose, string]>;

function createBlankTrackingEntry(): POTrackingEntry {
  return {
    id: `trk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    trackingNumber: "",
    carrier: "",
    purpose: "inbound",
    notes: "",
    createdAt: new Date().toISOString(),
  };
}

function normalizeTrackingEntries(entries: POTrackingEntry[]): POTrackingEntry[] {
  return entries
    .map((e) => ({
      ...e,
      trackingNumber: String(e.trackingNumber || "").trim(),
      carrier: String(e.carrier || "").trim() || undefined,
      purpose: e.purpose || "inbound",
      notes: String(e.notes || "").trim() || undefined,
      createdAt: e.createdAt || new Date().toISOString(),
    }))
    .filter((e) => e.trackingNumber.length > 0);
}

interface PlaceOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  registryProducts: RegistryProduct[];
  vendorSkuMappings: ProductRegistryService.ProductVendorSkuMapRow[];
  onSuccess: () => void;
}

interface DraftLineItem extends StandaloneOrderLineItem {
  _id: string;
}

type Step = 1 | 2 | 3;

const STEP_LABELS: Record<Step, string> = {
  1: "Order Details",
  2: "Materials",
  3: "Review & Submit",
};

export function PlaceOrderModal({
  open,
  onOpenChange,
  registryProducts,
  vendorSkuMappings,
  onSuccess,
}: PlaceOrderModalProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<Step>(1);
  const [description, setDescription] = useState("");
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [items, setItems] = useState<DraftLineItem[]>([]);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [externalPoByVendor, setExternalPoByVendor] = useState<Record<string, string>>({});
  const [trackingByVendor, setTrackingByVendor] = useState<Record<string, POTrackingEntry[]>>({});
  const [vendorNameOverride, setVendorNameOverride] = useState<Record<string, string>>({});
  const [taxByVendor, setTaxByVendor] = useState<Record<string, number>>({});

  // Group items by vendor using product registry vendor SKU mappings
  const vendorGroups = useMemo<StandaloneOrderVendorGroup[]>(() => {
    const byVendor = new Map<string, StandaloneOrderVendorGroup>();
    for (const item of items) {
      if (!item.sku || item.quantity <= 0) continue;
      const mapping = vendorSkuMappings.find(
        (m) => String(m.sku ?? "").toUpperCase() === item.sku.toUpperCase()
      );
      const vendorId = mapping?.vendor_id ?? "";
      const vendorName = mapping?.vendor_name ?? "Unknown Vendor";
      const key = vendorId || vendorName;
      const existing = byVendor.get(key);
      const lineItem: StandaloneOrderLineItem = {
        sku: item.sku,
        productName: item.productName,
        quantity: item.quantity,
        unitCost: item.unitCost,
      };
      if (existing) {
        existing.items.push(lineItem);
      } else {
        byVendor.set(key, { vendorId, vendorName, items: [lineItem] });
      }
    }
    return Array.from(byVendor.values()).map((vg) => ({
        ...vg,
      externalPoNumber: externalPoByVendor[vg.vendorId || vg.vendorName] ?? "",
      trackingNumbers: trackingByVendor[vg.vendorId || vg.vendorName] ?? [],
    }));
  }, [items, vendorSkuMappings, externalPoByVendor, trackingByVendor]);

  const hasUnknownVendor = vendorGroups.some(
    (vg) => vg.vendorName === "Unknown Vendor" && !(vendorNameOverride[vg.vendorId || vg.vendorName] ?? "").trim()
  );
  const missingExternalPo = vendorGroups.some(
    (vg) => !(externalPoByVendor[vg.vendorId || vg.vendorName] ?? "").trim()
  );
  const canSubmit = vendorGroups.length > 0 && !hasUnknownVendor && !missingExternalPo;
  const hasValidItems = items.filter((i) => i.sku && i.quantity > 0).length > 0;

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return registryProducts.slice(0, 20);
    const q = search.toLowerCase();
    return registryProducts
      .filter(
        (p) =>
          String(p.sku ?? "").toLowerCase().includes(q) ||
          String(p.productName ?? "").toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [search, registryProducts]);

  function addFromRegistry(product: RegistryProduct) {
    setItems((prev) => [
      ...prev,
      {
        _id: `${Date.now()}-${Math.random()}`,
        sku: String(product.sku ?? ""),
        productName: String(product.productName ?? ""),
        quantity: 1,
        unitCost: Number(product.cost ?? 0),
      },
    ]);
    setSearch("");
  }

  function addManual() {
    setItems((prev) => [
      ...prev,
      { _id: `${Date.now()}-${Math.random()}`, sku: "", productName: "", quantity: 1, unitCost: 0 },
    ]);
  }

  function updateItem(id: string, patch: Partial<DraftLineItem>) {
    setItems((prev) => prev.map((item) => (item._id === id ? { ...item, ...patch } : item)));
  }

  function removeItem(id: string) {
    setItems((prev) => prev.filter((item) => item._id !== id));
  }

  function reset() {
    setStep(1);
    setDescription("");
    setWarehouseId(null);
    setItems([]);
    setSearch("");
    setExternalPoByVendor({});
    setTrackingByVendor({});
    setVendorNameOverride({});
    setSubmitting(false);
  }

  function handleClose() {
    reset();
    onOpenChange(false);
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await StandaloneOrderService.create({
        description: description.trim() || undefined,
        warehouseId: warehouseId || undefined,
        vendorGroups: vendorGroups.map((vg) => {
          const key = vg.vendorId || vg.vendorName;
          const resolvedVendorName =
            vg.vendorName === "Unknown Vendor" && vendorNameOverride[key]?.trim()
              ? vendorNameOverride[key].trim()
              : vg.vendorName;
          return {
            ...vg,
            vendorName: resolvedVendorName,
            externalPoNumber: externalPoByVendor[key] ?? "",
            trackingNumbers: normalizeTrackingEntries(trackingByVendor[key] ?? []),
            tax: taxByVendor[key] ?? 0,
          };
        }),
      });
      if (!result) throw new Error("Create returned null");
      toast({
        title: "Order placed",
        description: `${result.refNumber} submitted successfully.`,
      });
      handleClose();
      onSuccess();
    } catch (err) {
      console.error("[PlaceOrderModal] submit failed:", err);
      toast({
        title: "Submission failed",
        description: "Could not place order. Try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Place Order
            <Badge variant="outline" className="text-[10px]">
              {STEP_LABELS[step]}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex gap-2 text-[10px] text-muted-foreground">
          {([1, 2, 3] as Step[]).map((s) => (
            <span key={s} className={s === step ? "font-semibold text-foreground" : ""}>
              {s}. {STEP_LABELS[s]}{s < 3 ? " ›" : ""}
            </span>
          ))}
        </div>

        {/* ── Step 1: Order Details ── */}
        {step === 1 && (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Description (optional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Tile stock replenishment"
                className="text-sm"
              />
              <p className="text-[10px] text-muted-foreground">
                A short label to identify this order in the queue.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                Destination warehouse <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <WarehouseSelector value={warehouseId} onChange={setWarehouseId} />
            </div>
          </div>
        )}

        {/* ── Step 2: Materials ── */}
        {step === 2 && (
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Search catalog</Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by SKU or name…"
                className="text-sm"
              />
              {search.trim().length > 0 && filteredProducts.length > 0 && (
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto bg-background shadow-sm">
                  {filteredProducts.map((p) => (
                    <button
                      key={String(p.sku ?? "")}
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/50 text-left"
                      onClick={() => addFromRegistry(p)}
                    >
                      <span className="font-mono text-muted-foreground w-24 shrink-0 truncate">
                        {p.sku}
                      </span>
                      <span className="flex-1 truncate">{p.productName}</span>
                      <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] h-7 w-28">SKU</TableHead>
                    <TableHead className="text-[10px] h-7">Item Name</TableHead>
                    <TableHead className="text-[10px] h-7 w-20 text-right">Qty</TableHead>
                    <TableHead className="text-[10px] h-7 w-28 text-right">Unit Cost</TableHead>
                    <TableHead className="text-[10px] h-7 w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell className="py-1">
                        <Input
                          value={item.sku}
                          onChange={(e) => updateItem(item._id, { sku: e.target.value })}
                          className="h-7 text-xs font-mono"
                          placeholder="SKU"
                        />
                      </TableCell>
                      <TableCell className="py-1">
                        <Input
                          value={item.productName}
                          onChange={(e) => updateItem(item._id, { productName: e.target.value })}
                          className="h-7 text-xs"
                          placeholder="Product name"
                        />
                      </TableCell>
                      <TableCell className="py-1">
                        <Input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(item._id, { quantity: Math.max(1, Number(e.target.value)) })
                          }
                          className="h-7 text-xs text-right tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </TableCell>
                      <TableCell className="py-1">
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          value={item.unitCost}
                          onChange={(e) =>
                            updateItem(item._id, { unitCost: Number(e.target.value) })
                          }
                          className="h-7 text-xs text-right tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </TableCell>
                      <TableCell className="py-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => removeItem(item._id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs h-7 gap-1"
              onClick={addManual}
            >
              <Plus className="h-3 w-3" /> Add item manually
            </Button>
          </div>
        )}

        {/* ── Step 3: Review & Submit ── */}
        {step === 3 && (
          <div className="space-y-3 py-2 max-h-96 overflow-y-auto">
            {vendorGroups.length === 0 && (
              <p className="text-sm text-muted-foreground">No valid materials to order.</p>
            )}
            {vendorGroups.map((vg) => {
              const key = vg.vendorId || vg.vendorName;
              const isUnknown = vg.vendorName === "Unknown Vendor";
              const displayName = isUnknown ? (vendorNameOverride[key] ?? "") : vg.vendorName;
              const unresolved = isUnknown && !displayName.trim();
              const groupSubtotal = vg.items.reduce(
                (sum, i) => sum + i.quantity * i.unitCost,
                0
              );
              const groupTax = taxByVendor[key] ?? 0;
              const groupTotal = groupSubtotal + groupTax;
              return (
                <div key={key} className={`border rounded-lg p-3 space-y-2 ${unresolved ? "border-destructive/40" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{displayName || "Unassigned vendor"}</span>
                    {unresolved && (
                      <Badge variant="destructive" className="text-[9px]">
                        Vendor required
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-0.5 pl-1">
                    {vg.items.map((item) => (
                      <div
                        key={item.sku}
                        className="flex items-center gap-2 text-[11px] text-muted-foreground"
                      >
                        <span className="font-mono w-24 shrink-0 truncate">{item.sku}</span>
                        <span className="flex-1 truncate">{item.productName}</span>
                        <span className="tabular-nums">×{item.quantity}</span>
                        <span className="tabular-nums">
                          ${(item.quantity * item.unitCost).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  {/* Subtotal + Tax + Grand Total */}
                  <div className="mt-2 pt-2 border-t border-border space-y-1">
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>Subtotal</span>
                      <span className="tabular-nums">${groupSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <label className="text-muted-foreground" htmlFor={`tax-${key}`}>Sales Tax ($)</label>
                      <input
                        id={`tax-${key}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={groupTax || ""}
                        placeholder="0.00"
                        onChange={(e) =>
                          setTaxByVendor((prev) => ({
                            ...prev,
                            [key]: Math.max(0, parseFloat(e.target.value) || 0),
                          }))
                        }
                        className="w-24 h-6 text-right text-[11px] tabular-nums border border-input rounded px-2 bg-background"
                      />
                    </div>
                    <div className="flex items-center justify-between text-[12px] font-semibold pt-1 border-t border-border">
                      <span>Total</span>
                      <span className="tabular-nums">${groupTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  {/* Vendor name override — shown only for unmatched SKU groups */}
                  {isUnknown && (
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Vendor name *
                      </Label>
                      <Input
                        value={vendorNameOverride[key] ?? ""}
                        onChange={(e) =>
                          setVendorNameOverride((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        placeholder="e.g. ABC Roofing Supply"
                        className="h-7 text-xs"
                        autoFocus
                      />
                      <p className="text-[10px] text-muted-foreground">
                        These SKUs aren't linked to a vendor in the Product Registry. Enter the vendor name to proceed.
                      </p>
                    </div>
                  )}

                  {/* External PO */}
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      External PO / Vendor Ref *
                    </Label>
                    <Input
                      value={externalPoByVendor[key] ?? ""}
                      onChange={(e) =>
                        setExternalPoByVendor((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      placeholder="Vendor PO, SO, or order #"
                      className="h-7 text-xs"
                    />
                  </div>

                  {/* Tracking numbers */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Tracking numbers
                        {(trackingByVendor[key] ?? []).length > 0 && (
                          <Badge variant="outline" className="ml-2 text-[9px]">
                            {(trackingByVendor[key] ?? []).length}
                          </Badge>
                        )}
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 text-[10px] gap-1 px-2"
                        onClick={() =>
                          setTrackingByVendor((prev) => ({
                            ...prev,
                            [key]: [...(prev[key] ?? []), createBlankTrackingEntry()],
                          }))
                        }
                      >
                        <Plus className="h-2.5 w-2.5" /> Add tracking
                      </Button>
                    </div>
                    {(trackingByVendor[key] ?? []).length === 0 ? (
                      <div className="rounded border border-dashed px-3 py-1.5 text-[11px] text-muted-foreground">
                        No tracking numbers — optional, can add now or later.
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {(trackingByVendor[key] ?? []).map((entry) => (
                          <div key={entry.id} className="grid gap-1.5 grid-cols-[100px_90px_1fr_auto]">
                            <select
                              value={entry.purpose ?? "inbound"}
                              onChange={(e) =>
                                setTrackingByVendor((prev) => ({
                                  ...prev,
                                  [key]: (prev[key] ?? []).map((row) =>
                                    row.id === entry.id
                                      ? { ...row, purpose: e.target.value as POTrackingPurpose }
                                      : row
                                  ),
                                }))
                              }
                              className="h-7 rounded-md border border-input bg-background px-2 text-xs"
                            >
                              {TRACKING_PURPOSE_OPTIONS.map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                            <Input
                              value={entry.carrier ?? ""}
                              onChange={(e) =>
                                setTrackingByVendor((prev) => ({
                                  ...prev,
                                  [key]: (prev[key] ?? []).map((row) =>
                                    row.id === entry.id ? { ...row, carrier: e.target.value } : row
                                  ),
                                }))
                              }
                              placeholder="Carrier"
                              className="h-7 text-xs"
                            />
                            <Input
                              value={entry.trackingNumber}
                              onChange={(e) =>
                                setTrackingByVendor((prev) => ({
                                  ...prev,
                                  [key]: (prev[key] ?? []).map((row) =>
                                    row.id === entry.id
                                      ? { ...row, trackingNumber: e.target.value }
                                      : row
                                  ),
                                }))
                              }
                              placeholder="Tracking number"
                              className="h-7 text-xs"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() =>
                                setTrackingByVendor((prev) => ({
                                  ...prev,
                                  [key]: (prev[key] ?? []).filter((row) => row.id !== entry.id),
                                }))
                              }
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {missingExternalPo && (
              <p className="text-xs text-destructive">
                All vendor groups require an external PO number before submitting.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-between items-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={step === 1 ? handleClose : () => setStep((s) => (s - 1) as Step)}
          >
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step < 3 ? (
            <Button
              type="button"
              size="sm"
              className="text-xs"
              disabled={step === 2 && !hasValidItems}
              onClick={() => setStep((s) => (s + 1) as Step)}
            >
              Next
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              className="text-xs"
              disabled={!canSubmit || submitting}
              onClick={() => void handleSubmit()}
            >
              {submitting ? "Submitting…" : "Submit Order"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
